import React from 'react'
import {
  InteriorQuality as InteriorQualityType,
  RealEstateCategory,
  Salutation,
  SegmentationType,
  TransactionType,
} from '../../../../../../@types/types'
import { Flow, FlowName, FlowScreenElement } from '../../../../../../@types/flow-types'
import {
  SCREEN_ADDRESS,
  SCREEN_BASIC_INFO,
  SCREEN_CONSTRUCTION,
  SCREEN_CONTACT_INFO,
  SCREEN_COSTS,
  SCREEN_DESCRIPTION,
  SCREEN_MULTIMEDIA,
  SCREEN_OTHER_FACILITIES,
  SCREEN_TENANT_DOCUMENTS,
  SCREENS,
} from '../../../../../../services/domain/wizardScreens'
import Address from '../../../../../../components/pages/insertionPage/screens/address/Address'
import { PROPERTY_OWNER_APARTMENT_RENT_FLOW_PATH } from '../../../../../../services/routes/routes'
import Description from '../../../../../../components/pages/insertionPage/screens/description/Description'
import ContactInfo from '../../../../../../components/pages/insertionPage/screens/contactInfo/ContactInfo'
import Multimedia from '../../../../../../components/pages/insertionPage/screens/multimediaNew/Multimedia'

import ResidentialCostsRent from '../../../../../../components/pages/insertionPage/screens/costs/wrappers/residential/ResidentialCostsRent'
import Construction from '../../../../../../components/pages/insertionPage/screens/construction/Construction'
import TenantDocumentsScreen from '../../../../../../components/pages/insertionPage/screens/tenantDocuments/TenantDocumentsScreen'
import { priceToApiMonthlyRent } from '../../../../../../api/realEstate/converters/toApi/estate/price/priceToApi'
import { MultimediaUploaderType } from '../../../../../../@types/multimedia-types'
import {
  bathroomField,
  certificateOfEligibilityNeeded,
  floorAndTotalFloor,
  interiorQuality,
  noBedrooms,
  parkingSpace,
  petsAllowed,
  usableAreaField,
} from '../../../../../../components/pages/insertionPage/screens/otherFacilities/wrapper/otherFacilitiesFieldsWrapper'
import OtherFacilities from '../../../../../../components/pages/insertionPage/screens/otherFacilities/OtherFacilities'
import { apartmentRentBuildingTypeField, apartmentRentEstateFeatures } from '../common'
import BasicInfo from '../../../../../../components/pages/insertionPage/screens/basicInfo/BasicInfo'
import {
  livingSpaceField,
  priceField,
  roomField,
} from '../../../../../../components/pages/insertionPage/screens/basicInfo/wrappers/basicInfoFieldsWrapper'
import { recommendedPriceNotification } from '../../../../../../components/pages/insertionPage/screens/basicInfo/wrappers/basicInfoSectionsWrapper'
import { PriceFieldType } from '../../../../../../components/pages/insertionPage/screens/basicInfo/fields/priceField/PriceField'
import { getDefaultClasses } from '../../../../../../utils/style-utils'
import { propertyOwnerApartmentRentSchema } from './propertyOwnerApartmentRentSchema'

const apartmentRentFlowScreens: FlowScreenElement[] = [
  {
    screen: SCREENS[SCREEN_ADDRESS],
    element: (props) => <Address {...props} isPriceOverviewLinkDisplayed={true} />,
  },
  {
    screen: SCREENS[SCREEN_BASIC_INFO],
    element: (props) => (
      <BasicInfo
        {...props}
        screenTooltipKey='tooltip.screens.basicInfo.apartmentHouseOrSTARent'
        properties={[
          livingSpaceField(),
          roomField(getDefaultClasses('right')),
          priceField(PriceFieldType.COLD_RENT, getDefaultClasses('left'), {
            showRecommendedPrefilledHint: true,
            showLabelWithTooltip: true,
            tooltipTranslationKey: 'wizard.screen.basicInfo.price.recommendedValuationTooltip',
          }),
          recommendedPriceNotification(),
        ]}
      />
    ),
  },
  {
    screen: SCREENS[SCREEN_COSTS],
    element: (props) => (
      <ResidentialCostsRent {...props} showMoveInDate={true} screenTooltipKey='tooltip.screens.costs.allRent' />
    ),
  },
  {
    screen: SCREENS[SCREEN_OTHER_FACILITIES],
    element: (props) => (
      <OtherFacilities
        {...props}
        screenTooltipKey='tooltip.screens.otherFacilities.allRent'
        properties={[
          apartmentRentEstateFeatures,
          interiorQuality(),
          certificateOfEligibilityNeeded(),
          petsAllowed(),
          apartmentRentBuildingTypeField,
          bathroomField(getDefaultClasses('left')),
          noBedrooms('right'),
          floorAndTotalFloor('left'),
          usableAreaField(undefined, getDefaultClasses('rightOnDesk')),
          parkingSpace({ showParkingSpaceType: true, hasGarageLabel: true }),
        ]}
      />
    ),
  },
  {
    screen: SCREENS[SCREEN_DESCRIPTION],
    element: (props) => (
      <Description
        {...props}
        hasFurnishingNote={true}
        hasLocationNote={true}
        hasOtherNote={true}
        hasHomeStreetHome={true}
      />
    ),
  },
  {
    screen: SCREENS[SCREEN_MULTIMEDIA],
    element: (props) => (
      <Multimedia
        {...props}
        doCloseMultimediaStatusMessage={props.doCloseMultimediaStatusMessage!}
        allowedTypesToDisplay={[
          MultimediaUploaderType.PICTURE,
          MultimediaUploaderType.FLOOR_PLAN,
          MultimediaUploaderType.DOCUMENT,
          MultimediaUploaderType.VIDEO,
        ]}
      />
    ),
  },
  {
    screen: SCREENS[SCREEN_CONSTRUCTION],
    element: (props) => <Construction {...props} hasLastModernizationField={true} />,
  },
  {
    screen: SCREENS[SCREEN_TENANT_DOCUMENTS],
    element: (props) => <TenantDocumentsScreen {...props} />,
  },
  {
    screen: SCREENS[SCREEN_CONTACT_INFO],
    element: (props) => <ContactInfo {...props} />,
  },
]

export const propertyOwnerApartmentRentFlow: Flow = {
  flowInformation: {
    owner: 'wizard.navigation.header.owner',
    estateType: 'wizard.navigation.header.apartment',
    offerType: 'wizard.navigation.header.rents',
    path: PROPERTY_OWNER_APARTMENT_RENT_FLOW_PATH,
    name: FlowName.PROPERTY_OWNER_APARTMENT_RENT,
    validationSchema: propertyOwnerApartmentRentSchema,
    options: {
      hasTextSuggestions: true,
      depositCalculationEnabled: true,
      recommendedPriceEnabled: true,
      hasHeyImmo: true,
      hasAiDataExtractionBanner: true,
    },
    metaData: {
      category: RealEstateCategory.APARTMENT,
      segmentation: SegmentationType.PROPERTY_OWNER,
      transaction: TransactionType.RENT,
    },
    priceToApiMapper: priceToApiMonthlyRent,
  },
  defaultStateValues: {
    heatingCostsOption: true,
    unknownConstructionYear: false,
    hideStreetAndStreetNumber: false,
    estateFeatures: {},
    interiorQuality: InteriorQualityType.NO_INFORMATION,
    salutation: Salutation.NO_SALUTATION,
  },
  flowScreens: apartmentRentFlowScreens,
}
